import { useRef } from 'react';
import { MdAttachFile } from "react-icons/md";
import { FILE_UPLOAD_CONSTANTS } from '../helpers/constants';
import { validateFile } from '../helpers/fileValidation';
import { useStrings } from '../i18n/useStrings';
import { formatString } from '../i18n/strings';
import './file-upload-button.css';

/**
 * File upload button component
 * Allows users to select and upload files to Salesforce chat
 * Supports multiple file selection
 *
 * @param {Function} onFilesSelect - Callback when files are selected (receives array of files)
 * @param {Function} onError - Callback when validation errors occur (receives error message)
 * @param {Boolean} disabled - Whether button should be disabled
 */
export default function FileUploadButton({ onFilesSelect, onError, disabled }) {
    const strings = useStrings();
    const fileInputRef = useRef(null);

    /**
     * Handle button click - trigger hidden file input
     */
    const handleFileClick = () => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    /**
     * Handle file selection from input
     * Validates each file and only passes valid ones to parent
     */
    const handleFileChange = (event) => {
        const files = event.target.files;

        if (!files || files.length === 0) {
            return;
        }

        const filesArray = Array.from(files);
        const validFiles = [];
        const errors = [];

        // Validate each file
        filesArray.forEach((file) => {
            const validation = validateFile(file);

            if (validation.valid) {
                validFiles.push(file);
            } else {
                // Collect error message
                let errorMsg;
                switch (validation.error) {
                    case 'FILE_TOO_LARGE':
                        errorMsg = formatString(
                            strings.fileTooLarge,
                            validation.details.sizeMB,
                            validation.details.maxSizeMB
                        );
                        break;
                    case 'INVALID_FILE_TYPE':
                        errorMsg = formatString(
                            strings.fileTypeNotSupported,
                            validation.details.fileType,
                            validation.details.supportedTypes
                        );
                        break;
                    default:
                        errorMsg = strings.fileUploadError;
                }
                errors.push(`${file.name}: ${errorMsg}`);
            }
        });

        // Show errors if any
        if (errors.length > 0 && onError) {
            onError(errors.join(' | '));
        }

        // Pass valid files to parent
        if (validFiles.length > 0) {
            onFilesSelect(validFiles);
        }

        // Reset input to allow selecting same files again
        event.target.value = '';
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                accept={FILE_UPLOAD_CONSTANTS.SUPPORTED_FILE_EXTENSIONS.join(',')}
                onChange={handleFileChange}
                aria-label={strings.attachFile}
            />
            <button
                className="attachButton"
                onClick={handleFileClick}
                disabled={disabled}
                title={strings.attachFile}
                aria-label={strings.attachFile}
            >
                <MdAttachFile className="attachButtonIcon" />
            </button>
        </>
    );
}
